import type { GetterTree } from 'vuex'
import type { RootState } from '@/store'
import type { Subscription, SubscriptionSKU } from '@/types/subscriptions'
import type { SubscriptionsState, EnrichedSubscription } from './types'
import { SUBSCRIPTIONS_GETTERS } from './types'
import { deviceModels } from './utils'

function canAddMoreDevicesToWebsite(state: SubscriptionsState): boolean {
  const used = totalWebsiteQtyUsed(state)
  return used < Number.MAX_SAFE_INTEGER
}

function websiteQtyRemainingByModel(state: SubscriptionsState, model: string): number {
  if (model === deviceModels.BLUESKY) {
    return state.websiteQtyRemainingBlueSky
  } else if (model === deviceModels.BLUESKY_GAS) {
    return state.websiteQtyRemainingBlueSkyGas
  } else if (model === deviceModels.AIRASSURE) {
    return state.websiteQtyRemainingAirAssure
  }
  return 0
}

function totalWebsiteQtyUsed(state: SubscriptionsState): number {
  return (
    state.websiteQtyUsedAirAssure + state.websiteQtyUsedBlueSky + state.websiteQtyUsedBlueSkyGas
  )
}

function totalWebsiteQtyRemaining(state: SubscriptionsState): number {
  return (
    state.websiteQtyRemainingAirAssure +
    state.websiteQtyRemainingBlueSky +
    state.websiteQtyRemainingBlueSkyGas
  )
}

interface ISubscriptionsGetters extends GetterTree<SubscriptionsState, RootState> {
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA](
    state: SubscriptionsState
  ): Record<string, SubscriptionSKU> | null
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_BY_ID](
    state: SubscriptionsState
  ): (skuID: string) => SubscriptionSKU | undefined
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_NAME_BY_ID](
    state: SubscriptionsState
  ): (skuID: string) => string
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_TIER_NAME_BY_ID](
    state: SubscriptionsState
  ): (skuID: string) => string
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_TYPE_NAME_BY_ID](
    state: SubscriptionsState
  ): (skuID: string) => string
  [SUBSCRIPTIONS_GETTERS.GET_GENERIC_TSI_LINK](state: SubscriptionsState): EnrichedSubscription[]
  [SUBSCRIPTIONS_GETTERS.GET_WEBSITE](state: SubscriptionsState): EnrichedSubscription[]
  [SUBSCRIPTIONS_GETTERS.GET_BASIC](state: SubscriptionsState): EnrichedSubscription[]
  [SUBSCRIPTIONS_GETTERS.GET_PREMIUM](state: SubscriptionsState): EnrichedSubscription[]
  [SUBSCRIPTIONS_GETTERS.GET_DATA_SERVICES](state: SubscriptionsState): EnrichedSubscription[]
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_WEBSITE](
    state: SubscriptionsState
  ): Record<string, string> | null
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_BASIC](state: SubscriptionsState): Record<string, string> | null
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_PREMIUM](
    state: SubscriptionsState
  ): Record<string, string> | null
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_DATA_SERVICES](
    state: SubscriptionsState
  ): Record<string, string> | null
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_SUBSCRIPTION_BY_DEVICE_SERIAL](
    state: SubscriptionsState
  ): (serial: string) => string | null
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_BASIC_SUBSCRIPTION_BY_DEVICE_SERIAL](
    state: SubscriptionsState
  ): (serial: string) => string | undefined
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_PREMIUM_SUBSCRIPTION_BY_DEVICE_SERIAL](
    state: SubscriptionsState
  ): (serial: string) => string | undefined
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_DATA_SERVICES_BY_DEVICE_SERIAL](
    state: SubscriptionsState
  ): (serial: string) => string | undefined
  [SUBSCRIPTIONS_GETTERS.GET_SUBSCRIPTION_BY_ID](
    state: SubscriptionsState
  ): (id: string) => Subscription | undefined
  [SUBSCRIPTIONS_GETTERS.GET_QTY_REMAINING](state: SubscriptionsState): (model: string) => number
  [SUBSCRIPTIONS_GETTERS.HAS_QTY_REMAINING](state: SubscriptionsState): boolean
  [SUBSCRIPTIONS_GETTERS.HAS_DATA_SERVICES_SUBSCRIPTION](
    state: SubscriptionsState
  ): (serial: string) => boolean
  [SUBSCRIPTIONS_GETTERS.HAS_WEBSITE_SUBSCRIPTION](
    state: SubscriptionsState
  ): (serial: string) => boolean
  [SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_WEBSITE_BY_MODEL](
    state: SubscriptionsState
  ): (model: string) => boolean
  [SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_BASIC_BY_MODEL](
    state: SubscriptionsState
  ): (model: string) => boolean
  [SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL](
    state: SubscriptionsState
  ): (model: string) => boolean
  [SUBSCRIPTIONS_GETTERS.GET_PERMISSIONS_BY_ROLE](
    state: SubscriptionsState
  ): (role: string) => string[] | undefined
}

const getters: ISubscriptionsGetters = {
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA](state) {
    return state.SKUMetadata
  },
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_BY_ID](state) {
    return (skuID: string) => state.SKUMetadata?.[skuID]
  },
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_NAME_BY_ID](state) {
    return (skuID: string) => {
      const sku = state.SKUMetadata?.[skuID]
      if (sku) {
        return sku.name
      }
      return ''
    }
  },
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_TIER_NAME_BY_ID](state) {
    return (skuID: string) => {
      const sku = state.SKUMetadata?.[skuID]
      if (sku) {
        return sku.tier_name
      }
      return ''
    }
  },
  [SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_TYPE_NAME_BY_ID](state) {
    return (skuID: string) => {
      const sku = state.SKUMetadata?.[skuID]
      if (sku) {
        return sku.type_name
      }
      return ''
    }
  },
  [SUBSCRIPTIONS_GETTERS.GET_GENERIC_TSI_LINK](state) {
    return state.genericTSILink
  },
  [SUBSCRIPTIONS_GETTERS.GET_WEBSITE](state) {
    return state.website
  },
  [SUBSCRIPTIONS_GETTERS.GET_BASIC](state) {
    return state.basic
  },
  [SUBSCRIPTIONS_GETTERS.GET_PREMIUM](state) {
    return state.premium
  },
  [SUBSCRIPTIONS_GETTERS.GET_DATA_SERVICES](state) {
    return state.dataServices
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_WEBSITE](state) {
    return state.deviceWebsite
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_BASIC](state) {
    return state.deviceBasic
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_PREMIUM](state) {
    return state.devicePremium
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_DATA_SERVICES](state) {
    return state.deviceDataServices
  },
  // Returns the device subscription by serial number, checking both website and generic subscriptions
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_SUBSCRIPTION_BY_DEVICE_SERIAL](state) {
    return (serial: string) =>
      state.deviceWebsite?.[serial] ?? state.deviceGeneric?.[serial] ?? null
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_BASIC_SUBSCRIPTION_BY_DEVICE_SERIAL](state) {
    return (serial: string) => state.deviceBasic?.[serial]
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_PREMIUM_SUBSCRIPTION_BY_DEVICE_SERIAL](state) {
    return (serial: string) => state.devicePremium?.[serial]
  },
  [SUBSCRIPTIONS_GETTERS.GET_DEVICE_DATA_SERVICES_BY_DEVICE_SERIAL](state) {
    return (serial: string) => state.deviceDataServices?.[serial]
  },
  [SUBSCRIPTIONS_GETTERS.GET_SUBSCRIPTION_BY_ID](state) {
    return (id: string) => state.subscriptions.find((subscription) => subscription.id === id)
  },
  [SUBSCRIPTIONS_GETTERS.GET_QTY_REMAINING](state) {
    return (model: string) => {
      if (!canAddMoreDevicesToWebsite(state)) {
        return 0
      }

      let qtyRemaining = 0
      if (state.website.length !== 0) {
        qtyRemaining = websiteQtyRemainingByModel(state, model)
      }
      return Math.min(Number.MAX_SAFE_INTEGER, qtyRemaining)
    }
  },
  [SUBSCRIPTIONS_GETTERS.HAS_QTY_REMAINING](state) {
    return canAddMoreDevicesToWebsite(state) && totalWebsiteQtyRemaining(state) > 0
  },
  [SUBSCRIPTIONS_GETTERS.HAS_DATA_SERVICES_SUBSCRIPTION](state) {
    return (serial: string) => !!state.deviceDataServices?.[serial]
  },
  [SUBSCRIPTIONS_GETTERS.HAS_WEBSITE_SUBSCRIPTION](state) {
    return (serial: string) => !!state.deviceWebsite?.[serial]
  },
  [SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_WEBSITE_BY_MODEL](state) {
    return (model: string) => {
      if (model === deviceModels.BLUESKY) {
        return state.websiteQtyRemainingBlueSky > 0
      } else if (model === deviceModels.BLUESKY_GAS) {
        return state.websiteQtyRemainingBlueSkyGas > 0
      } else if (model === deviceModels.AIRASSURE) {
        return state.websiteQtyRemainingAirAssure > 0
      }

      // No subscription found at all, default to false
      return false
    }
  },
  [SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_BASIC_BY_MODEL](state) {
    return (model: string) => {
      if (model === deviceModels.BLUESKY) {
        return state.basicQtyRemainingBlueSky > 0
      } else if (model === deviceModels.AIRASSURE) {
        return state.basicQtyRemainingAirAssure > 0
      }

      // No subscription found at all, default to false
      return false
    }
  },
  [SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL](state) {
    return (model: string) => {
      if (model === deviceModels.BLUESKY) {
        return state.premiumQtyRemainingBlueSky > 0
      } else if (model === deviceModels.BLUESKY_GAS) {
        return state.premiumQtyRemainingBlueSkyGas > 0
      } else if (model === deviceModels.AIRASSURE) {
        return state.premiumQtyRemainingAirAssure > 0
      }

      // No subscription found at all, default to false
      return false
    }
  },
  [SUBSCRIPTIONS_GETTERS.GET_PERMISSIONS_BY_ROLE](state) {
    return (role: string) => state.permissionsInRole[role]
  }
}

export default getters
